import { create } from "zustand";
import type { LayoutType, BrandConfig, TenantConfig } from "../config/env.js";
import { getBrandConfig, getCurrentTenantConfig, resolveDomainProfile } from "../config/env.js";

// 动态导入品牌资源（避免循环依赖）
let getBrandAssetsFunc: ((tenantCode?: string) => { logo: string; solidLogo: string; favicon: string }) | null = null;

/**
 * 注入品牌资源获取函数
 * 由应用在启动时调用，传入 @ifa/ui 的 getBrandAssets 函数
 */
export function setBrandAssetsProvider(
  provider: (tenantCode?: string) => { logo: string; solidLogo: string; favicon: string }
) {
  getBrandAssetsFunc = provider;
}

/**
 * 合并品牌配置和品牌资源
 */
function mergeBrandConfigWithAssets(
  config: BrandConfig,
  tenantCode?: string
): BrandConfig {
  if (getBrandAssetsFunc) {
    const assets = getBrandAssetsFunc(tenantCode);
    return {
      ...config,
      logo: assets.logo,
      solidLogo: assets.solidLogo,
      favicon: assets.favicon,
    };
  }
  return config;
}

interface ConfigState {
  // 布局类型
  layoutType: LayoutType;
  // 品牌配置
  brandConfig: BrandConfig;
  // 租户配置
  tenantConfig: {
    config: TenantConfig | null;
    isGenericSSO: boolean;
    requireManualTenantInput: boolean;
  };
  // 是否已初始化
  initialized: boolean;

  // 初始化配置（基于 callback URL，用于 SSO 登录页）
  initializeConfig: (callbackUrl: string) => void;
  // 初始化配置（基于当前域名，用于业务应用）
  initializeFromCurrentDomain: () => void;
  // 更新布局类型
  setLayoutType: (layoutType: LayoutType) => void;
  // 更新品牌配置（基于租户码）
  updateBrandConfig: (tenantCode?: string) => void;
}

export const useConfigStore = create<ConfigState>((set, get) => ({
  layoutType: "web",
  brandConfig: getBrandConfig(),
  tenantConfig: getCurrentTenantConfig(),
  initialized: false,

  initializeConfig: (callbackUrl: string) => {
    const profile = resolveDomainProfile({ source: "callback", value: callbackUrl });
    const tenantConfig = {
      config: profile.tenantConfig,
      isGenericSSO: profile.isGenericSSO,
      requireManualTenantInput: profile.requireManualTenantInput,
    };
    const brandConfig = mergeBrandConfigWithAssets(
      profile.brandConfig,
      profile.tenantCode
    );

    set({
      layoutType: profile.layoutType,
      brandConfig,
      tenantConfig,
      initialized: true,
    });

    console.log("[ConfigStore] 配置已初始化（基于 callback URL）:", {
      layoutType: profile.layoutType,
      brandConfig: brandConfig.name,
      tenantCode: tenantConfig.config?.tenantCode,
    });
  },

  initializeFromCurrentDomain: () => {
    const hostname = typeof window !== "undefined" ? window.location.host : "";
    const profile = resolveDomainProfile({ source: "domain", value: hostname });
    const tenantConfig = {
      config: profile.tenantConfig,
      isGenericSSO: profile.isGenericSSO,
      requireManualTenantInput: profile.requireManualTenantInput,
    };
    const brandConfig = mergeBrandConfigWithAssets(
      profile.brandConfig,
      profile.tenantCode
    );

    set({
      layoutType: profile.layoutType,
      brandConfig,
      tenantConfig,
      initialized: true,
    });

    console.log("[ConfigStore] 配置已初始化（基于当前域名）:", {
      hostname: profile.hostname,
      layoutType: profile.layoutType,
      brandConfig: brandConfig.name,
      tenantCode: tenantConfig.config?.tenantCode,
    });
  },

  setLayoutType: (layoutType: LayoutType) => {
    set({ layoutType });
  },

  updateBrandConfig: (tenantCode?: string) => {
    const brandConfig = getBrandConfig(tenantCode);
    set({ brandConfig });
  },
}));
